import { AlertTriangle, XCircle, CheckCircle2 } from 'lucide-react'
import Modal from '@/components/common/Modal'
import type { ApprovalValidationIssue } from '@/livepreview/api'

interface Props {
  open: boolean
  onClose: () => void
  issues: ApprovalValidationIssue[]
}

/**
 * Renders the result of validating a workflow against the engine's executable
 * subset — errors first, then warnings, each with its message and the offending
 * activity/transition id (`where`). An empty issue list reads as "valid".
 */
export default function ValidationResultModal({ open, onClose, issues }: Props) {
  const errors = issues.filter(i => i.severity === 'error')
  const warnings = issues.filter(i => i.severity === 'warning')

  return (
    <Modal open={open} onClose={onClose} title="Workflow validation" size="wide">
      {issues.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', color: 'var(--color-text)' }}>
          <CheckCircle2 size={18} style={{ color: 'var(--color-success, #1a7f37)' }} />
          <span style={{ fontSize: '0.9125rem' }}>This workflow is valid and ready to publish.</span>
        </div>
      ) : (
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {errors.length > 0 && (
            <Section title={`Errors (${errors.length})`} color="#f44336">
              {errors.map((iss, i) => (
                <IssueRow key={`e-${i}`} issue={iss} icon={<XCircle size={14} style={{ color: 'var(--color-error, #cf222e)', flexShrink: 0, marginTop: 2 }} />} />
              ))}
            </Section>
          )}
          {warnings.length > 0 && (
            <Section title={`Warnings (${warnings.length})`} color="#ff9800">
              {warnings.map((iss, i) => (
                <IssueRow key={`w-${i}`} issue={iss} icon={<AlertTriangle size={14} style={{ color: 'var(--color-warning, #9a6700)', flexShrink: 0, marginTop: 2 }} />} />
              ))}
            </Section>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose} className="btn btn-primary btn-sm">Close</button>
      </div>
    </Modal>
  )
}

function Section({ title, color, children }: { title: string; color: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <h4 style={{ margin: '0 0 6px', fontSize: '0.8625rem', fontWeight: 600, color }}>{title}</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </div>
  )
}

function IssueRow({ issue, icon }: { issue: ApprovalValidationIssue; icon: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', gap: 8, padding: '6px 8px',
      background: 'var(--color-bg-secondary)', borderRadius: 4,
      border: '1px solid var(--color-border)',
    }}>
      {icon}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.8625rem', color: 'var(--color-text)', lineHeight: 1.4 }}>{issue.message}</div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', opacity: 0.7, marginTop: 2 }}>
          {issue.code}{issue.where ? ` · at ${issue.where}` : ''}
        </div>
      </div>
    </div>
  )
}
